import {apiPost} from './index';

export const PVM_ACCOUNT_IMPACT_TYPES = ['volumeNewCustomer', 'volumeLostCustomer'];

const getGroupId = (record, groupBy) => {
    if (record.group_id != null) {
        return record.group_id;
    }
    if (groupBy === 'hq_account_id') {
        return record.hq_account_id;
    }
    if (groupBy === 'account_id') {
        return record.account_id;
    }
    if (groupBy === 'sales_person_id') {
        return record.sales_person_id;
    }
    return record.product_id;
};

const getGroupName = (record, groupBy) => {
    if (groupBy === 'product_id') {
        return record.product_name;
    }
    if (groupBy === 'account_id') {
        return record.account_name;
    }
    if (groupBy === 'hq_account_id') {
        return record.hq_account_name;
    }
    if (groupBy === 'sales_person_id') {
        return record.sales_person_name;
    }
    return record.product_name;
};

const getGroupCode = (record, groupBy) => {
    if (groupBy === 'product_id') {
        return record.product_sku;
    }
    if (groupBy === 'account_id') {
        return record.account_number;
    }
    if (groupBy === 'hq_account_id') {
        return record.hq_account_number;
    }
    if (groupBy === 'sales_person_id') {
        return record.sales_person_id != null ? String(record.sales_person_id) : null;
    }
    return record.product_sku;
};

const formatHierarchy = (record, groupBy) => {
    if (groupBy === 'product_id') {
        const names = record.product_hierarchy_names;
        if (Array.isArray(names)) {
            return names.join(' / ');
        }
        return names || null;
    }
    if (groupBy === 'account_id' || groupBy === 'hq_account_id') {
        const names = record.account_hierarchy_names;
        if (Array.isArray(names)) {
            return names.join(' / ');
        }
        return names || null;
    }
    const names = groupBy === 'product_id'
        ? record.product_hierarchy_names
        : record.account_hierarchy_names;
    if (Array.isArray(names)) {
        return names.join(' / ');
    }
    if (names) {
        return names;
    }
    if (groupBy === 'hq_account_id') {
        return record.hq_account_name || null;
    }
    return null;
};

const isExpandableGroupBy = (groupBy) => groupBy === 'hq_account_id' || groupBy === 'account_id';

export const buildBreakdownComponents = (calcTypes, calcsMeta = {}) => calcTypes.map((type) => ({
    field: type,
    name: calcsMeta[type]?.name || type,
    valueType: calcsMeta[type]?.valueType
}));

export const mergeBreakdownPeriods = (currentData, priorData, calcTypes, parentId = null, options = {}) => {
    const includePriorOnly = options.includePriorOnly === true;
    const groupBy = currentData?.groupBy || priorData?.groupBy || 'hq_account_id';
    const priorById = new Map(
        (priorData?.records || []).map((record) => [String(getGroupId(record, groupBy)), record])
    );
    const currentById = new Map(
        (currentData?.records || []).map((record) => [String(getGroupId(record, groupBy)), record])
    );

    const components = buildBreakdownComponents(calcTypes, currentData?.calcs || priorData?.calcs || {});

    const sourceRecords = includePriorOnly
        ? [...new Set([...currentById.keys(), ...priorById.keys()])].map((id) => currentById.get(id) || priorById.get(id))
        : (currentData?.records || []);

    const rows = sourceRecords.map((record) => {
        const id = String(getGroupId(record, groupBy));
        const priorRecord = priorById.get(id);
        const currentRecord = currentById.get(id);
        const displayRecord = currentRecord || priorRecord || record;
        const metrics = {};

        for (const type of calcTypes) {
            const current = parseFloat(currentRecord?.[type]) || 0;
            const prior = parseFloat(priorRecord?.[type]) || 0;
            metrics[type] = {
                current,
                prior,
                delta: current - prior,
                valueType: currentRecord?.[`${type}_valueType`]
                    || priorRecord?.[`${type}_valueType`]
                    || currentData?.calcs?.[type]?.valueType
            };
        }

        return {
            id: parentId ? `${parentId}:${id}` : id,
            parentId,
            groupId: id,
            name: getGroupName(displayRecord, groupBy),
            code: getGroupCode(displayRecord, groupBy),
            hierarchy: formatHierarchy(displayRecord, groupBy),
            expandable: isExpandableGroupBy(groupBy),
            metrics
        };
    });

    return {groupBy, components, rows};
};

const sumRecordField = (records, field) => (records || []).reduce(
    (total, record) => total + (parseFloat(record?.[field]) || 0),
    0
);

const buildMetricsFromRecords = (currentRecords, priorRecords, calcTypes, calcsMeta = {}) => {
    const metrics = {};
    const currentRevenue = sumRecordField(currentRecords, 'net_revenue');
    const priorRevenue = sumRecordField(priorRecords, 'net_revenue');
    const currentVolume = sumRecordField(currentRecords, 'volume');
    const priorVolume = sumRecordField(priorRecords, 'volume');
    const currentMargin = sumRecordField(currentRecords, 'pocket_price_margin');
    const priorMargin = sumRecordField(priorRecords, 'pocket_price_margin');

    for (const type of calcTypes) {
        let current = 0;
        let prior = 0;
        let valueType = calcsMeta[type]?.valueType;

        if (type === 'net_price') {
            current = currentVolume ? currentRevenue / currentVolume : 0;
            prior = priorVolume ? priorRevenue / priorVolume : 0;
        } else if (type === 'pocket_price_margin_percent') {
            current = currentRevenue ? currentMargin / currentRevenue : 0;
            prior = priorRevenue ? priorMargin / priorRevenue : 0;
        } else {
            current = sumRecordField(currentRecords, type);
            prior = sumRecordField(priorRecords, type);
            valueType = valueType
                || currentRecords?.[0]?.[`${type}_valueType`]
                || priorRecords?.[0]?.[`${type}_valueType`];
        }

        metrics[type] = {
            current,
            prior,
            delta: current - prior,
            valueType
        };
    }

    return metrics;
};

const getRecordKey = (record, groupBy) => (
    groupBy === 'account_id' ? String(record.account_id) : String(record.product_id)
);

const groupRecordsByDimension = (currentRecords, priorRecords, groupBy) => {
    const groups = new Map();

    const ensureGroup = (key) => {
        if (!groups.has(key)) {
            groups.set(key, {
                currentRecords: [],
                priorRecords: [],
                displayRecord: null
            });
        }
        return groups.get(key);
    };

    for (const record of currentRecords || []) {
        const key = getRecordKey(record, groupBy);
        const group = ensureGroup(key);
        group.currentRecords.push(record);
        group.displayRecord = group.displayRecord || record;
    }
    for (const record of priorRecords || []) {
        const key = getRecordKey(record, groupBy);
        const group = ensureGroup(key);
        group.priorRecords.push(record);
        group.displayRecord = group.displayRecord || record;
    }

    return groups;
};

const groupRecordsByProductId = (currentRecords, priorRecords) => {
    const pairs = new Map();

    const ensurePair = (productId) => {
        if (!pairs.has(productId)) {
            pairs.set(productId, {current: null, prior: null});
        }
        return pairs.get(productId);
    };

    for (const record of currentRecords || []) {
        ensurePair(String(record.product_id)).current = record;
    }
    for (const record of priorRecords || []) {
        ensurePair(String(record.product_id)).prior = record;
    }

    return pairs;
};

const buildPvmProductChildRow = ({
    accountId,
    productId,
    currentRecord,
    priorRecord,
    calcTypes,
    calcsMeta
}) => {
    const displayRecord = currentRecord || priorRecord;

    return {
        id: `${accountId}:${productId}`,
        parentId: accountId,
        groupId: productId,
        name: getGroupName(displayRecord, 'product_id'),
        code: getGroupCode(displayRecord, 'product_id'),
        hierarchy: formatHierarchy(displayRecord, 'product_id'),
        expandable: false,
        metrics: buildMetricsFromRecords(
            currentRecord ? [currentRecord] : [],
            priorRecord ? [priorRecord] : [],
            calcTypes,
            calcsMeta
        )
    };
};

const buildPvmAccountParentRow = ({
    accountId,
    group,
    calcTypes,
    calcsMeta,
    childCount
}) => ({
    id: accountId,
    parentId: null,
    groupId: accountId,
    name: getGroupName(group.displayRecord, 'account_id'),
    code: getGroupCode(group.displayRecord, 'account_id'),
    hierarchy: formatHierarchy(group.displayRecord, 'account_id'),
    expandable: childCount > 0,
    metrics: buildMetricsFromRecords(
        group.currentRecords,
        group.priorRecords,
        calcTypes,
        calcsMeta
    )
});

const buildPvmAccountProductTableData = (currentData, priorData, calcTypes) => {
    const calcsMeta = currentData?.calcs || priorData?.calcs || {};
    const components = buildBreakdownComponents(calcTypes, calcsMeta);
    const groupedAccounts = groupRecordsByDimension(
        currentData?.records,
        priorData?.records,
        'account_id'
    );
    const rows = [];

    for (const [accountId, group] of groupedAccounts.entries()) {
        const productPairs = groupRecordsByProductId(group.currentRecords, group.priorRecords);
        const childRows = [...productPairs.entries()]
            .map(([productId, pair]) => buildPvmProductChildRow({
                accountId,
                productId,
                currentRecord: pair.current,
                priorRecord: pair.prior,
                calcTypes,
                calcsMeta
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

        rows.push(buildPvmAccountParentRow({
            accountId,
            group,
            calcTypes,
            calcsMeta,
            childCount: childRows.length
        }));
        rows.push(...childRows);
    }

    rows.sort((a, b) => {
        if (a.parentId == null && b.parentId == null) {
            return a.name.localeCompare(b.name);
        }
        return 0;
    });

    return {groupBy: 'account_id', components, rows};
};

const buildPvmFlatTableData = (currentData, priorData, calcTypes, groupBy) => {
    const calcsMeta = currentData?.calcs || priorData?.calcs || {};
    const components = buildBreakdownComponents(calcTypes, calcsMeta);
    const grouped = groupRecordsByDimension(
        currentData?.records,
        priorData?.records,
        groupBy
    );

    const rows = [...grouped.entries()]
        .map(([id, group]) => ({
            id,
            parentId: null,
            groupId: id,
            name: getGroupName(group.displayRecord, groupBy),
            code: getGroupCode(group.displayRecord, groupBy),
            hierarchy: formatHierarchy(group.displayRecord, groupBy),
            expandable: false,
            metrics: buildMetricsFromRecords(
                group.currentRecords,
                group.priorRecords,
                calcTypes,
                calcsMeta
            )
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

    return {groupBy, components, rows};
};

export const buildPvmImpactTableData = (currentData, priorData, calcTypes, impactType) => {
    if (PVM_ACCOUNT_IMPACT_TYPES.includes(impactType)) {
        return buildPvmAccountProductTableData(currentData, priorData, calcTypes);
    }

    return buildPvmFlatTableData(currentData, priorData, calcTypes, 'product_id');
};

export const fetchCalcBreakdown = (queryParams, calcTypes, callback) => {
    apiPost(`v2/calc/breakdown${queryParams}`, {calcTypes}, callback);
};

export const fetchCalcBreakdownMerged = (queryParams, calcTypes, callback) => {
    fetchCalcBreakdown(queryParams, calcTypes, (resp) => {
        callback(resp?.success ? resp.data : null);
    });
};

export const fetchCalcBreakdownBothPeriods = (currentQueryParams, priorQueryParams, calcTypes, callback, parentId = null) => {
    let currentData = null;
    let priorData = null;

    const done = () => {
        if (!currentData || !priorData) {
            return;
        }
        callback(mergeBreakdownPeriods(currentData, priorData, calcTypes, parentId));
    };

    fetchCalcBreakdown(currentQueryParams, calcTypes, (resp) => {
        currentData = resp.success ? resp.data : {records: [], calcs: {}, groupBy: 'hq_account_id'};
        done();
    });

    fetchCalcBreakdown(priorQueryParams, calcTypes, (resp) => {
        priorData = resp.success ? resp.data : {records: [], calcs: {}, groupBy: currentData?.groupBy || 'hq_account_id'};
        done();
    });
};
